import { z } from 'zod';
import { AdminClubTelegramSourceSchema } from './admin-clubs.js';

export const AdminClubInboxRawItemStatusSchema = z.enum(['pending', 'processed', 'error']);
export type AdminClubInboxRawItemStatus = z.infer<typeof AdminClubInboxRawItemStatusSchema>;

export const AdminClubInboxMessageTypeSchema = z.enum([
  'text',
  'image',
  'video',
  'audio',
  'document',
  'unknown',
]);
export type AdminClubInboxMessageType = z.infer<typeof AdminClubInboxMessageTypeSchema>;

export const AdminClubInboxUpdateKindSchema = z.enum(['channel_post', 'edited_channel_post']);
export type AdminClubInboxUpdateKind = z.infer<typeof AdminClubInboxUpdateKindSchema>;

export const AdminClubInboxMediaTypeSchema = z.enum([
  'image',
  'video',
  'video_note',
  'audio',
  'document',
]);
export type AdminClubInboxMediaType = z.infer<typeof AdminClubInboxMediaTypeSchema>;

export const AdminClubInboxProcessActionSchema = z.enum(['processed']);
export type AdminClubInboxProcessAction = z.infer<typeof AdminClubInboxProcessActionSchema>;

export const AdminClubInboxQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20).optional(),
  status: AdminClubInboxRawItemStatusSchema.optional(),
  messageType: AdminClubInboxMessageTypeSchema.optional(),
});
export type AdminClubInboxQuery = z.infer<typeof AdminClubInboxQuerySchema>;

export const AdminClubInboxPackQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(20).optional(),
  status: AdminClubInboxRawItemStatusSchema.default('pending').optional(),
  messageType: AdminClubInboxMessageTypeSchema.optional(),
});
export type AdminClubInboxPackQuery = z.infer<typeof AdminClubInboxPackQuerySchema>;

export const AdminClubInboxSourceDiagnosticsSchema = z.object({
  source: AdminClubTelegramSourceSchema.nullable(),
  totalItems: z.number(),
  pendingItems: z.number(),
  processedItems: z.number(),
  errorItems: z.number(),
  latestTelegramDate: z.string().nullable(),
});
export type AdminClubInboxSourceDiagnostics = z.infer<typeof AdminClubInboxSourceDiagnosticsSchema>;

export const AdminClubInboxListItemSchema = z.object({
  id: z.string(),
  sourceId: z.string(),
  clubId: z.string().nullable(),
  telegramMessageId: z.number().int(),
  telegramDate: z.string(),
  telegramEditDate: z.string().nullable(),
  updateKind: AdminClubInboxUpdateKindSchema,
  messageType: AdminClubInboxMessageTypeSchema,
  status: AdminClubInboxRawItemStatusSchema,
  previewText: z.string().nullable(),
  firstSeenAt: z.string(),
  processedAt: z.string().nullable(),
  lastError: z.string().nullable(),
  mediaCount: z.number().int(),
});
export type AdminClubInboxListItem = z.infer<typeof AdminClubInboxListItemSchema>;

export const AdminClubInboxListResponseSchema = z.object({
  items: z.array(AdminClubInboxListItemSchema),
  nextCursor: z.string().optional(),
  sourceDiagnostics: AdminClubInboxSourceDiagnosticsSchema,
});
export type AdminClubInboxListResponse = z.infer<typeof AdminClubInboxListResponseSchema>;

const AdminClubInboxCountByStatusSchema = z.object({
  pending: z.number().int(),
  processed: z.number().int(),
  error: z.number().int(),
});
export type AdminClubInboxCountByStatus = z.infer<typeof AdminClubInboxCountByStatusSchema>;

const AdminClubInboxCountByMessageTypeSchema = z.object({
  text: z.number().int(),
  image: z.number().int(),
  video: z.number().int(),
  audio: z.number().int(),
  document: z.number().int(),
  unknown: z.number().int(),
});
export type AdminClubInboxCountByMessageType = z.infer<
  typeof AdminClubInboxCountByMessageTypeSchema
>;

export const AdminClubInboxPackMediaReadinessSchema = z.object({
  mediaCount: z.number().int(),
  readyMediaCount: z.number().int(),
  missingAssetCount: z.number().int(),
  unsupportedItemCount: z.number().int(),
  skippableItemCount: z.number().int(),
  processable: z.boolean(),
  failureReasons: z.array(z.string()),
  skippableReasons: z.array(z.string()),
});
export type AdminClubInboxPackMediaReadiness = z.infer<
  typeof AdminClubInboxPackMediaReadinessSchema
>;

export const AdminClubInboxPackSchema = z.object({
  id: z.string(),
  sourceId: z.string(),
  clubId: z.string(),
  startTelegramDate: z.string(),
  endTelegramDate: z.string(),
  itemCount: z.number().int(),
  statusCounts: AdminClubInboxCountByStatusSchema,
  messageTypeCounts: AdminClubInboxCountByMessageTypeSchema,
  mediaReadiness: AdminClubInboxPackMediaReadinessSchema,
  items: z.array(AdminClubInboxListItemSchema),
});
export type AdminClubInboxPack = z.infer<typeof AdminClubInboxPackSchema>;

export const AdminClubInboxPackListResponseSchema = z.object({
  packs: z.array(AdminClubInboxPackSchema),
  sourceDiagnostics: AdminClubInboxSourceDiagnosticsSchema,
});
export type AdminClubInboxPackListResponse = z.infer<typeof AdminClubInboxPackListResponseSchema>;

export const AdminClubInboxDetailMediaSchema = z.object({
  id: z.string(),
  type: AdminClubInboxMediaTypeSchema,
  telegramFileId: z.string(),
  telegramFileUniqueId: z.string().nullable(),
  mimeType: z.string().nullable(),
  fileName: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  durationSeconds: z.number().nullable(),
  publicUrl: z.string().nullable(),
});
export type AdminClubInboxDetailMedia = z.infer<typeof AdminClubInboxDetailMediaSchema>;

export const AdminClubInboxProcessLogSchema = z.object({
  id: z.string(),
  action: AdminClubInboxProcessActionSchema,
  actorUserId: z.string().nullable(),
  chapterId: z.string().nullable(),
  chapterTitle: z.string().nullable(),
  createdBlockIds: z.array(z.string()),
  createdAt: z.string(),
});
export type AdminClubInboxProcessLog = z.infer<typeof AdminClubInboxProcessLogSchema>;

export const AdminClubInboxDetailItemSchema = AdminClubInboxListItemSchema.extend({
  text: z.string().nullable(),
  caption: z.string().nullable(),
  mediaGroupId: z.string().nullable(),
  rawPayload: z.unknown(),
  updatedAt: z.string(),
  media: z.array(AdminClubInboxDetailMediaSchema),
  latestProcess: AdminClubInboxProcessLogSchema.nullable(),
});
export type AdminClubInboxDetailItem = z.infer<typeof AdminClubInboxDetailItemSchema>;

export const AdminClubInboxDetailResponseSchema = z.object({
  item: AdminClubInboxDetailItemSchema,
  sourceDiagnostics: AdminClubInboxSourceDiagnosticsSchema,
});
export type AdminClubInboxDetailResponse = z.infer<typeof AdminClubInboxDetailResponseSchema>;

export const AdminClubInboxCreateChapterTargetSchema = z.object({
  title: z.string().trim().min(1).max(300),
  subtitle: z.string().trim().max(50).optional(),
});
export type AdminClubInboxCreateChapterTarget = z.infer<
  typeof AdminClubInboxCreateChapterTargetSchema
>;

export const AdminClubInboxProcessRequestSchema = z
  .object({
    existingChapterId: z.string().uuid().optional(),
    createChapter: AdminClubInboxCreateChapterTargetSchema.optional(),
  })
  .superRefine((value, context) => {
    const hasExisting = Boolean(value.existingChapterId);
    const hasCreate = Boolean(value.createChapter);
    if (hasExisting === hasCreate) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Choose either existingChapterId or createChapter',
        path: ['existingChapterId'],
      });
    }
  });
export type AdminClubInboxProcessRequest = z.infer<typeof AdminClubInboxProcessRequestSchema>;

export const AdminClubInboxProcessResponseSchema = z.object({
  itemId: z.string(),
  status: AdminClubInboxRawItemStatusSchema,
  chapterId: z.string(),
  chapterTitle: z.string(),
  createdBlockIds: z.array(z.string()),
  processLog: AdminClubInboxProcessLogSchema,
});
export type AdminClubInboxProcessResponse = z.infer<typeof AdminClubInboxProcessResponseSchema>;

export const AdminClubInboxPackProcessRequestSchema = z.object({
  createChapter: AdminClubInboxCreateChapterTargetSchema,
});
export type AdminClubInboxPackProcessRequest = z.infer<
  typeof AdminClubInboxPackProcessRequestSchema
>;

export const AdminClubInboxPackProcessResponseSchema = z.object({
  packId: z.string(),
  status: z.literal('processed'),
  chapterId: z.string(),
  chapterTitle: z.string(),
  processedItemIds: z.array(z.string()),
  createdBlockIds: z.array(z.string()),
  processLogs: z.array(AdminClubInboxProcessLogSchema),
});
export type AdminClubInboxPackProcessResponse = z.infer<
  typeof AdminClubInboxPackProcessResponseSchema
>;
